import { supabase } from '../lib/supabase';

// CRITICAL FIX: Get effective user ID for all queries
const getEffectiveUserId = () => {
  const impersonationData = localStorage.getItem('admin_impersonation');
  if (impersonationData) {
    const { impersonatedUserId } = JSON.parse(impersonationData);
    return impersonatedUserId;
  }
  return null;
};

/**
 * ROI Calculator Service
 * Handles all database operations for the ROI calculator configuration
 */

// Add this helper function
const toCamelCaseArray = (data) => {
  if (!data) return [];
  return data?.map(item => ({
    id: item?.id,
    configName: item?.config_name,
    monthlyRevenue: item?.monthly_revenue,
    profitMarginPercentage: item?.profit_margin_percentage,
    averageOrderValue: item?.average_order_value,
    numberOfProducts: item?.number_of_products,
    monthlyShippingCost: item?.monthly_shipping_cost,
    desiredRevenue: item?.desired_revenue,
    totalPpcBudget: item?.total_ppc_budget,
    currency: item?.currency,
    isActive: item?.is_active,
    createdAt: item?.created_at,
    updatedAt: item?.updated_at,
    userId: item?.user_id
  }));
};

export const roiCalculatorService = {
  /**
   * Get all configurations for the current user
   */
  async getConfigs() {
    try {
      const effectiveUserId = getEffectiveUserId();
      
      let query = supabase
        ?.from('roi_calculator_configs')
        ?.select(`
          *,
          platform_settings(*),
          marketing_services(*),
          financial_configs(*)
        `)
        ?.order('created_at', { ascending: false });
      
      if (effectiveUserId) {
        query = query?.eq('user_id', effectiveUserId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return toCamelCaseArray(data);
    } catch (error) {
      console.error('Error fetching ROI configs:', error);
      throw error;
    }
  },

  /**
   * Alias for getConfigs() for backwards compatibility
   * Get all configurations for the current user
   */
  async getAllConfigs() {
    return this.getConfigs();
  },

  /**
   * Get a single configuration by ID
   */
  async getConfigById(configId) {
    try {
      const { data: config, error: configError } = await supabase?.from('roi_calculator_configs')?.select(`
          *,
          platform_settings(*),
          marketing_services(*),
          financial_configs(*)
        `)?.eq('id', configId)?.single();

      if (configError) throw configError;

      // Convert to camelCase
      return {
        id: config?.id,
        configName: config?.config_name,
        monthlyRevenue: config?.monthly_revenue,
        profitMarginPercentage: config?.profit_margin_percentage,
        averageOrderValue: config?.average_order_value,
        numberOfProducts: config?.number_of_products,
        monthlyShippingCost: config?.monthly_shipping_cost,
        desiredRevenue: config?.desired_revenue || 0,
        totalPpcBudget: config?.total_ppc_budget || 0,
        currency: config?.currency,
        isActive: config?.is_active,
        platformSettings: {
          platform: config?.platform_settings?.platform,
          hostingCost: config?.platform_settings?.hosting_cost,
          monthlySubscription: config?.platform_settings?.monthly_subscription,
          platformFeePercentage: config?.platform_settings?.platform_fee_percentage,
          transactionFeePercentage: config?.platform_settings?.transaction_fee_percentage,
          paymentGatewayFeePercentage: config?.platform_settings?.payment_gateway_fee_percentage,
          shopifyPlan: config?.platform_settings?.shopify_plan
        },
        marketingServices: {
          marketingManagementType: config?.marketing_services?.marketing_management_type,
          monthlyRetainer: config?.marketing_services?.monthly_retainer || 0,
          includeOrganicFacebookInstagram: config?.marketing_services?.include_organic_facebook_instagram || false,
          includePaidMeta: config?.marketing_services?.include_paid_meta || false,
          includePaidGoogleAds: config?.marketing_services?.include_paid_google_ads || false,
          includePaidMicrosoftAds: config?.marketing_services?.include_paid_microsoft_ads || false,
          includeNewsletters: config?.marketing_services?.include_newsletters || false,
          includeWebDev: config?.marketing_services?.include_web_dev || false,
          additionalOverheads: config?.marketing_services?.additional_overheads || 0
        },
        financialConfig: {
          vatPercentage: config?.financial_configs?.vat_percentage,
          profitMarginPercentage: config?.financial_configs?.profit_margin_percentage,
          overheadPercentage: config?.financial_configs?.overhead_percentage
        }
      };
    } catch (error) {
      console.error('Error fetching config:', error);
      throw error;
    }
  },

  /**
   * Create a new configuration
   */
  async createConfig(configData) {
    try {
      // Convert config data to snake_case for database
      const dbConfig = {
        config_name: configData?.configName,
        monthly_revenue: configData?.monthlyRevenue,
        profit_margin_percentage: configData?.profitMarginPercentage,
        average_order_value: configData?.averageOrderValue,
        number_of_products: configData?.numberOfProducts,
        monthly_shipping_cost: configData?.monthlyShippingCost,
        desired_revenue: configData?.desiredRevenue || 0,
        total_ppc_budget: configData?.totalPpcBudget || 0,
        currency: configData?.currency,
        is_active: configData?.isActive
      };

      const { data: config, error: configError } = await supabase?.from('roi_calculator_configs')?.insert([dbConfig])?.select()?.single();

      if (configError) throw configError;

      // Create platform settings
      const dbPlatform = {
        config_id: config?.id,
        platform: configData?.platformSettings?.platform,
        hosting_cost: configData?.platformSettings?.hostingCost,
        monthly_subscription: configData?.platformSettings?.monthlySubscription,
        platform_fee_percentage: configData?.platformSettings?.platformFeePercentage,
        transaction_fee_percentage: configData?.platformSettings?.transactionFeePercentage,
        payment_gateway_fee_percentage: configData?.platformSettings?.paymentGatewayFeePercentage,
        shopify_plan: configData?.platformSettings?.shopifyPlan
      };

      const { error: platformError } = await supabase?.from('platform_settings')?.insert([dbPlatform]);

      if (platformError) throw platformError;

      // Create marketing services
      const dbMarketing = {
        config_id: config?.id,
        marketing_management_type: configData?.marketingServices?.marketingManagementType,
        monthly_retainer: configData?.marketingServices?.monthlyRetainer || 0,
        include_organic_facebook_instagram: configData?.marketingServices?.includeOrganicFacebookInstagram || false,
        include_paid_meta: configData?.marketingServices?.includePaidMeta || false,
        include_paid_google_ads: configData?.marketingServices?.includePaidGoogleAds || false,
        include_paid_microsoft_ads: configData?.marketingServices?.includePaidMicrosoftAds || false,
        include_newsletters: configData?.marketingServices?.includeNewsletters || false,
        include_web_dev: configData?.marketingServices?.includeWebDev || false,
        additional_overheads: configData?.marketingServices?.additionalOverheads || 0
      };

      const { error: marketingError } = await supabase?.from('marketing_services')?.insert([dbMarketing]);

      if (marketingError) throw marketingError;

      // Create financial config
      const dbFinancial = {
        config_id: config?.id,
        vat_percentage: configData?.financialConfig?.vatPercentage,
        profit_margin_percentage: configData?.financialConfig?.profitMarginPercentage,
        overhead_percentage: configData?.financialConfig?.overheadPercentage
      };

      const { error: financialError } = await supabase?.from('financial_configs')?.insert([dbFinancial]);

      if (financialError) throw financialError;

      // Return full config
      return await this.getConfigById(config?.id);
    } catch (error) {
      console.error('Error creating ROI config:', error);
      throw error;
    }
  },

  /**
   * Update an existing configuration
   */
  async updateConfig(configId, configData) {
    try {
      // Update main config
      const dbConfig = {
        config_name: configData?.configName,
        monthly_revenue: configData?.monthlyRevenue,
        profit_margin_percentage: configData?.profitMarginPercentage,
        average_order_value: configData?.averageOrderValue,
        number_of_products: configData?.numberOfProducts,
        monthly_shipping_cost: configData?.monthlyShippingCost,
        desired_revenue: configData?.desiredRevenue || 0,
        total_ppc_budget: configData?.totalPpcBudget || 0,
        currency: configData?.currency,
        is_active: configData?.isActive
      };

      const { error: configError } = await supabase?.from('roi_calculator_configs')?.update(dbConfig)?.eq('id', configId);

      if (configError) throw configError;

      // Update platform settings
      const dbPlatform = {
        platform: configData?.platformSettings?.platform,
        hosting_cost: configData?.platformSettings?.hostingCost,
        monthly_subscription: configData?.platformSettings?.monthlySubscription,
        platform_fee_percentage: configData?.platformSettings?.platformFeePercentage,
        transaction_fee_percentage: configData?.platformSettings?.transactionFeePercentage,
        payment_gateway_fee_percentage: configData?.platformSettings?.paymentGatewayFeePercentage,
        shopify_plan: configData?.platformSettings?.shopifyPlan
      };

      const { error: platformError } = await supabase?.from('platform_settings')?.update(dbPlatform)?.eq('config_id', configId);

      if (platformError) throw platformError;

      // Update marketing services
      const dbMarketing = {
        marketing_management_type: configData?.marketingServices?.marketingManagementType,
        monthly_retainer: configData?.marketingServices?.monthlyRetainer || 0,
        include_organic_facebook_instagram: configData?.marketingServices?.includeOrganicFacebookInstagram || false,
        include_paid_meta: configData?.marketingServices?.includePaidMeta || false,
        include_paid_google_ads: configData?.marketingServices?.includePaidGoogleAds || false,
        include_paid_microsoft_ads: configData?.marketingServices?.includePaidMicrosoftAds || false,
        include_newsletters: configData?.marketingServices?.includeNewsletters || false,
        include_web_dev: configData?.marketingServices?.includeWebDev || false,
        additional_overheads: configData?.marketingServices?.additionalOverheads || 0
      };

      const { error: marketingError } = await supabase?.from('marketing_services')?.update(dbMarketing)?.eq('config_id', configId);

      if (marketingError) throw marketingError;

      // Update financial config
      const dbFinancial = {
        vat_percentage: configData?.financialConfig?.vatPercentage,
        profit_margin_percentage: configData?.financialConfig?.profitMarginPercentage,
        overhead_percentage: configData?.financialConfig?.overheadPercentage
      };

      const { error: financialError } = await supabase?.from('financial_configs')?.update(dbFinancial)?.eq('config_id', configId);

      if (financialError) throw financialError;

      // Return updated config
      return await this.getConfigById(configId);
    } catch (error) {
      console.error('Error updating ROI config:', error);
      throw error;
    }
  },

  /**
   * Delete a configuration
   */
  async deleteConfig(configId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('roi_calculator_configs')?.delete()?.eq('id', configId)?.eq('user_id', user?.id);

    if (error) throw error;
    return { success: true };
  }
};